import React, { useEffect, useState } from 'react';

import { Button, ErrorPage, Icon } from '@tecma/ds';
import { useTranslation } from '@tecma/i18n';
import { useDispatch, useSelector } from 'react-redux';

import { configurationsActions } from 'store/sagas/configurations/configurations.actions';
import { applicationStateSelectors } from 'store/slices/applicationState/selectors';
import { selectCurrentUser } from 'store/slices/auth/selectors';
import { configurationsSelectors } from 'store/slices/configurations/selectors';

import { CardApartment } from 'components/CardApartment';
import { FooterSection } from 'components/FooterSection/FooterSection';
import { ContactUsModal } from 'components/ContactUsModal';
import { PageLoader } from 'components/PageLoader';
import { EmptySection } from 'components/EmptySection/EmptySection';

import { addConfigurationRent } from 'utils/addConfigurationRent';
import { redirectToConfigurator } from 'utils/redirectToConfigurator';

import './card-apartment-container.scss';

interface CardApartmentContainerRequiredProps {}

// Optional Props
interface CardApartmentContainerOptionalProps {}

// Combined required and optional props to build the full prop interface
export interface CardApartmentContainerProps extends CardApartmentContainerRequiredProps, CardApartmentContainerOptionalProps {}

const CardApartmentContainer: React.FC<CardApartmentContainerProps> = () => {
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const isLoading = useSelector(configurationsSelectors.selectIsLoading);
  const hasError = useSelector(configurationsSelectors.selectHasError);
  const configurations = useSelector(configurationsSelectors.selectConfigurations);
  const area = useSelector(applicationStateSelectors.selectArea);
  const assetType = useSelector(applicationStateSelectors.selectAssetTypology);
  const spaceConfigurator = useSelector(applicationStateSelectors.selectSpaceConfigurator);
  const coliving = useSelector(applicationStateSelectors.selectColiving);

  const [isContactUsModalOpen, setIsContactUsModalOpen] = useState(false);

  const toggleContactUsModal = () => {
    setIsContactUsModalOpen(!isContactUsModalOpen);
  };

  useEffect(() => {
    dispatch(configurationsActions.initConfigurations());
  }, []);

  if (hasError) return <ErrorPage />;

  return (
    <>
      <div className='card-apartment-container'>
        <div className='card-apartment-container-header'>
          <div className='card-apartment-container-header-greetings'>
            <Icon iconName='user' size='large' />
            <h2>
              {t('card-apartment-container.greetings')},
              <br />
              <span>{`${user?.firstName} ${user?.lastName}`}</span>
            </h2>
          </div>
          <span className='description'>{t('card-apartment-container.description')}</span>
        </div>
        {isLoading && <PageLoader />}
        {!isLoading && !configurations?.length ? (
          <EmptySection onClick={() => redirectToConfigurator({ assetType, spaceConfigurator, coliving })} />
        ) : (
          <div className='cards-apartment'>
            {configurations?.map((config) => <CardApartment {...config} quotes={config.quotes} key={config.id} />)}
          </div>
        )}
        {configurations?.length ? (
          spaceConfigurator?.enabled ? (
            <div className='add-configuration-section'>
              <span>{t('card-apartment-container.add-configuration-section.title')}</span>
              <Button onClick={() => addConfigurationRent(spaceConfigurator)} iconName='plus' outlined>
                {t('card-apartment-container.add-configuration-section.button-label')}
              </Button>
            </div>
          ) : (
            area === 'rent' && <FooterSection showContactUsModal={toggleContactUsModal} />
          )
        ) : null}
      </div>
      <ContactUsModal isOpen={isContactUsModalOpen} toggleModal={toggleContactUsModal} />
    </>
  );
};

export default React.memo(CardApartmentContainer);
